// ScreenRouter.js — Maps screen names to components
// Extracted from App.js to keep it manageable
import React, { Suspense } from 'react';
import { LoadingSpinner } from './utils/appUtils';

// All screens are already lazy-loaded in App.js and passed as props
export default function ScreenRouter({
  screen,
  // shared props
  profile, xp, streak, neevCoins, completedCounts, isPremium, user,
  // handlers
  setScreen, addXP, addCoins, handleUpgrade, handleLogout, handleGradeChange, handleDailyChallenge,
  // screen components (lazy)
  HomeScreen, ScenarioScreen, QuizWorld, StoriesScreen, ChallengesScreen, NewsScreen,
  MoneySchoolHome, OlympiadScreen, MathsScreen, ScienceStemWorld, ScienceWorldHome, StemScreen,
  AITutorScreen, MockTestScreen, MockTestGate, LeaderboardScreen, CertificateScreen,
  LanguageScreen, GamesScreen, CreatorStudio, MultiplayerQuiz, NeevPetWorld,
  CardCollection, AchievementFeedFull, HomeworkHelper, SubjectPracticeScreen,
  StudentProgressScreen, ProfileScreen, ParentReportScreen,
  // home-specific
  homeRefreshTick, openSpin, openShop, openChat, todayXPEarned,
}) {
  const back = () => setScreen('home');

  const renderScreen = () => {
    switch (screen) {
      case 'home': return <HomeScreen onNavigate={setScreen} profile={profile} xp={xp} streak={streak}
        isPremium={isPremium} onUpgrade={handleUpgrade} onDailyChallenge={handleDailyChallenge}
        completedCounts={completedCounts} onXPEarned={addXP} coins={neevCoins} onCoinsEarned={addCoins}
        onOpenSpin={openSpin} onOpenShop={openShop} missionRefreshTick={homeRefreshTick}
        onOpenChat={openChat} user={user} todayXPEarned={todayXPEarned}/>;
      case 'entrepreneurship': return <ScenarioScreen onBack={back} onXPEarned={addXP} profile={profile}/>;
      case 'quiz': return <QuizWorld onBack={back} onXPEarned={addXP} onCoinsEarned={addCoins} profile={profile}/>;
      case 'stories': return <StoriesScreen onBack={back} onXPEarned={addXP} profile={profile}/>;
      case 'challenges': return <ChallengesScreen onBack={back} onXPEarned={addXP} profile={profile} onNavigate={setScreen}/>;
      case 'news': return <NewsScreen onBack={back} onXPEarned={addXP} profile={profile}/>;
      case 'money': return <MoneySchoolHome onBack={back} onXPEarned={addXP} profile={profile} isPremium={isPremium} onUpgrade={handleUpgrade}/>;
      case 'olympiad': return <OlympiadScreen onBack={back} onXPEarned={addXP} profile={profile} isPremium={isPremium} onUpgrade={handleUpgrade}/>;
      case 'maths': return <MathsScreen onBack={back} onXPEarned={addXP} profile={profile}/>;
      case 'stem':
      case 'science': return <ScienceStemWorld onBack={back} onXPEarned={addXP} profile={profile}
        ScienceWorldHome={ScienceWorldHome} StemScreen={StemScreen} defaultTab={screen==='stem'?'stem':'science'}/>;
      case 'ai_tutor': return <AITutorScreen onBack={back} onXPEarned={addXP} profile={profile} onNavigate={setScreen}/>;
      case 'quiz_bot': return <QuizWorld onBack={back} onXPEarned={addXP} onCoinsEarned={addCoins} profile={profile}/>;
      case 'mocktest': return (completedCounts?.olympiad||0)>=5
        ? <MockTestScreen onBack={back} onXPEarned={addXP} profile={profile} isPremium={isPremium} onUpgrade={handleUpgrade}/>
        : <MockTestGate onBack={back} done={completedCounts?.olympiad||0} onGoOlympiad={()=>setScreen('olympiad')}/>;
      case 'leaderboard': return <LeaderboardScreen onBack={back} profile={profile} xp={xp} user={user} isPremium={isPremium} onUpgrade={handleUpgrade}/>;
      case 'certificate': return <CertificateScreen onBack={back} profile={profile} xp={xp} completedCounts={completedCounts}/>;
      case 'language': return <LanguageScreen onBack={back} onXPEarned={addXP} profile={profile}/>;
      case 'spotit':
      case 'games': return <GamesScreen onBack={back} onXPEarned={addXP} profile={profile} openSpotIt={screen==='spotit'}/>;
      case 'creator_studio': return <CreatorStudio onBack={back} user={user} profile={profile} onXPEarned={addXP} onCoinsEarned={addCoins}/>;
      case 'multiplayer': return <MultiplayerQuiz onBack={back} profile={profile} onXPEarned={addXP} onCoinsEarned={addCoins}/>;
      case 'neevpet': return <NeevPetWorld onBack={back} xp={xp} streak={streak} coins={neevCoins} onXPEarned={addXP} onCoinsEarned={addCoins} profile={profile}/>;
      case 'cards': return <CardCollection onBack={back}/>;
      case 'achievefeed': return <AchievementFeedFull userId={user?.id} onBack={back}/>;
      case 'brain': return <QuizWorld onBack={back} onXPEarned={addXP} onCoinsEarned={addCoins} profile={profile}/>;
      case 'homework': return <HomeworkHelper onBack={back} onXPEarned={addXP} profile={profile} isPremium={isPremium} onUpgrade={handleUpgrade}/>;
      case 'subject_practice': return <SubjectPracticeScreen onBack={back} onNavigate={setScreen} onXPEarned={addXP} profile={profile}/>;
      case 'my_progress': return <StudentProgressScreen onBack={back} profile={profile}/>;
      case 'profile': return <ProfileScreen onBack={back} profile={profile} xp={xp} streak={streak}
        onLogout={handleLogout} onGradeChange={handleGradeChange} completedCounts={completedCounts}
        onNavigate={setScreen} onCoinsEarned={addCoins} coins={neevCoins}/>;
      case 'parent_report': return <ParentReportScreen onBack={()=>setScreen('profile')} profile={profile} xp={xp} streak={streak} completedCounts={completedCounts}/>;
      default: return <HomeScreen onNavigate={setScreen} profile={profile} xp={xp} streak={streak}
        isPremium={isPremium} onUpgrade={handleUpgrade} onXPEarned={addXP} coins={neevCoins}
        onCoinsEarned={addCoins} onOpenSpin={openSpin}/>;
    }
  };

  return (
    <Suspense fallback={<LoadingSpinner/>}>
      {renderScreen()}
    </Suspense>
  );
}
